/** Mirrors of the C# PPA DTO records, field for field. */

/*
  THE EXPENSE CLASSES LIVE IN `lib/expenseClass` from 06 Oct 2026, when the logistics
  issuance form's fund cluster became the second surface drawing them — a logistics
  component importing from this slice would cross a feature boundary to reach three words.

  RE-EXPORTED rather than left behind, so every call site in this suite keeps the import
  path it already had: one definition, and no sweep of seven files to reach it.
*/
export {
  PPA_EXPENSE_CLASS,
  PPA_EXPENSE_CLASS_LABEL,
  PPA_EXPENSE_CLASS_SHORT,
} from '../../../lib/expenseClass';
export type { PpaExpenseClass } from '../../../lib/expenseClass';

/**
 * How far one department has got with its budget under one plan.
 *
 * **Derived server-side — there is no column and no row.** "For review" here means the
 * window closed with something filed, NOT that the office declared itself finished: this
 * product has no per-department submit, and the reference's does. See
 * `PpaDepartmentProgresses` for what building one would cost.
 */
export const PPA_DEPARTMENT_PROGRESS = {
  notStarted: 0,
  onGoing: 1,
  forReview: 2,
  /**
   * Every fund the department proposed against carries an approved ceiling.
   *
   * The one stage only Budget Review can set, and a member of this map rather than a fourth
   * word invented beside it — the `StockStatus.NotTracked` call, where the gap was in the
   * model rather than in the label. It reads the same on the plan's own PPA tab and on the
   * reviewer's register, because both draw one roster.
   */
  budgetFinalization: 3,
} as const;

/** One enrolled department on a plan's roster, with its proposal rolled up. */
export interface BudgetPlanDepartmentRow {
  /** The ENROLMENT's id, not the unit's — this row is one line of a roster. */
  budgetDepartmentId: string;
  orgUnitId: string;
  orgUnitName: string;
  orgUnitCode: string | null;
  progress: number;
  /** Resolved server-side, so no client re-derives the words. */
  progressLabel: string;
  ppaCount: number;
  /**
   * How many budgeted LINES the department has filed, across its PPAs.
   *
   * Not the same fact as `ppaCount` — one PPA carrying twenty lines and twenty carrying one
   * read identically without it. ONE count rather than three: the per-class trio shipped
   * here on 21 Sep 2026 and nothing rendered it, which is a wire contract promising data no
   * surface asked for. Per-class counts live on `PpaListItem`, where the profile's folder
   * badges read them.
   */
  totalItems: number;
  /*
    NO fund-source column, from 06 Oct 2026: the fund source IS the expense class, and the
    three amounts below already say which classes this department is funding.
  */
  personnelServices: number;
  mooe: number;
  capitalOutlay: number;
  total: number;

  /**
   * What Budget Review has approved across every fund, and how far it is from the ask.
   *
   * **Columns on THIS row, not a second record and not a second surface.** The reference
   * draws the plan's PPA tab and Budget Review's department list with one component over one
   * DTO, and this is that commitment paid: the tab shipped without the approved band on
   * 21 Sep 2026 and gained it here when the review module landed.
   *
   * Null where nothing is priced, which is a different fact from a ceiling of zero — a
   * reviewer may genuinely approve nothing against a fund, and an em dash must not read as
   * that decision having been taken.
   */
  approvedCeiling: number | null;
  deviation: number | null;
  /**
   * Whether EVERY fund this department proposed against carries a ceiling.
   *
   * Server-derived, so the marker on the row and the refusal on the Approve button cannot
   * disagree about which departments are holding the plan up. **Never re-derived here**: a
   * department priced on one fund of two is not done, and comparing `approvedCeiling`
   * against `total` would call it done the moment the two figures happened to match.
   */
  isPriced: boolean;
}

/** One PPA as the plan's panel lists it. Every name is resolved through a join. */
export interface PpaListItem {
  id: string;
  ppaNumber: string;
  budgetPlanId: string;
  /** The plan's own number, resolved live — the profile's breadcrumb names it. */
  budgetPlanNumber: string;
  orgUnitId: string;
  orgUnitName: string;
  name: string;
  goalId: string | null;
  goalName: string | null;
  /** The GOAL's own objective and strategy, resolved live — never the PPA's. */
  goalObjective: string | null;
  goalStrategy: string | null;
  /** Where the goal sits in the hierarchy, for the profile's meta line. */
  sectorName: string | null;
  subSectorName: string | null;
  /**
   * The PPA's FUND SOURCE — an expense class (`PPA_EXPENSE_CLASS`), from 06 Oct 2026. Every
   * line follows it. Null only on a PPA filed before the field existed with nothing under it.
   */
  expenseClass: number | null;
  /** Resolved server-side, so no client re-derives the words. */
  expenseClassLabel: string | null;
  expectedOutcome: string | null;
  personnelServices: number;
  mooe: number;
  capitalOutlay: number;
  total: number;
  /** The line COUNT per class — the profile's folder badges read these. */
  personnelServicesItems: number;
  mooeItems: number;
  capitalOutlayItems: number;
  /** Get-only server-side, over the three. */
  lineItemCount: number;
  /**
   * Whether this PPA may still be written to.
   *
   * Derived from the PLAN's stage server-side, so the panel's disabled reason and the
   * endpoint's refusal cannot disagree. Never re-derived on this side.
   */
  isEditable: boolean;
  isActive: boolean;
  createdAt: string;
  createdByName: string | null;
  updatedAt: string | null;
  updatedByName: string | null;
  /**
   * The reviewer's approved ceiling for this row's (department, expense class), and what is
   * left of it after every active PPA drawing on that class.
   *
   * **NULL is UNPRICED, never a ceiling of zero** — nobody has looked yet, where zero is a
   * reviewer approving nothing. The column renders the em dash for the first and a muted
   * `0.00` for the second, which is what keeps the two apart.
   *
   * The ceiling belongs to the FUND rather than to one PPA, so every row under one fund
   * reports the same pair — the reference's merged cell, drawn per row because `<DataTable>`
   * cannot row-span.
   */
  ceilingApproved: number | null;
  ceilingRemaining: number | null;
}

/** One budgeted line. Its class is the PPA's `expenseClass` — a line carries none of its own. */
export interface PpaLineItemDto {
  id: string;
  item: string;
  budgetAccountCodeId: string | null;
  accountCode: string | null;
  accountDescription: string | null;
  /** Calendar dates — `YYYY-MM-DD`, never an instant. */
  startDate: string | null;
  endDate: string | null;
  amount: number;
  /** Null is "never adjusted", which is not the same fact as an adjustment of zero. */
  adjustedAmount: number | null;
  /** The figure that COUNTS — the adjustment where there is one, else the ask. */
  effectiveAmount: number;
  isExcluded: boolean;
  /** Whether the line is live. A DELETED one is listed only under Show deleted. */
  isActive: boolean;
  sortOrder: number;
}

export interface PpaDetail {
  ppa: PpaListItem;
  lineItems: PpaLineItemDto[];
}

/**
 * One budgeted line on the wire.
 *
 * **No `category`** — the class is the PPA's fund source, read off the PPA by the server.
 * **No `sortOrder`**, because a position is assigned and never
 * asked for. **No `adjustedAmount` and no `isExcluded`**: both are Budget Review's, and a
 * department's own form must not be able to write the verdict on its own ask.
 */
export interface PpaLineItemInput {
  item: string;
  budgetAccountCodeId: string | null;
  /** Calendar dates — `YYYY-MM-DD`, never an instant. */
  startDate: string | null;
  endDate: string | null;
  amount: number;
}

/** Adds several lines, all under the PPA's own expense class — the request names none. */
export interface PpaLineItemsCreateRequest {
  items: PpaLineItemInput[];
}

/** Corrects ONE line. The class is the PPA's, so a line cannot leave it. */
export type PpaLineItemUpdateRequest = PpaLineItemInput;

/**
 * Create and update take the same shape — the PPA's OWN facts.
 *
 * **The lines do NOT travel with it.** A PPA is created empty and filled a class at a time,
 * which is why its lines are a sub-resource with their own ids: replacing the set on every
 * save would destroy a reviewer's adjustment each time a department added a line.
 */
export interface PpaRequest {
  budgetPlanId: string;
  orgUnitId: string;
  name: string;
  goalId: string | null;
  /** The fund source, as a `PPA_EXPENSE_CLASS` number. Required — the server refuses null. */
  expenseClass: number | null;
  expectedOutcome: string | null;
}

/**
 * One ACTIVE budgeted line of a plan, for the Expenditures roll-up.
 *
 * A PLAN-LEVEL view of every office's lines at once, grouped by expense class and banded by
 * department — the question neither the department table nor a PPA profile can answer: what
 * the whole fiscal year is asking for, without opening sixty PPAs.
 *
 * **`amount` is the EFFECTIVE figure** — the reviewer's adjustment where there is one and the
 * proposal where there is not — because that is what every other total on this plan sums. The
 * proposed figure here would make the roll-up disagree with the header three tabs away.
 */
export interface PpaExpenditureLine {
  id: string;
  ppaId: string;
  ppaNumber: string;
  ppaName: string;
  orgUnitId: string;
  orgUnitName: string;
  /** The expense class — the PPA's, which every line follows — as the int the groups are keyed on. */
  category: number;
  item: string;
  accountCode: string | null;
  amount: number;
}
